// Wrapper for faker.js

import faker from 'faker';
import { sprintf } from 'sprintf-js';

const TDA_data = require("./TDA_constants.json");

const relationships = [
  "brother", "sister", "husband", "wife",
  "son", "daughter", "aunt", "uncle",
  "niece", "nephew", "cousin",
  "brother in-law", "sister in-law",
  "father in-law", "mother in-law",
];

const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const UPPERCASE = LOWERCASE.toUpperCase();
const DIGITS = "0123456789";
const SPECIAL_CHARACTERS = ".,<>!@#$%^&*()+=_-[]{}";
const ALL_CHARS = LOWERCASE + UPPERCASE + DIGITS + SPECIAL_CHARACTERS;

const ROOT = {

  date: {
    /**
     * @param years {int} date range in years
     * @param start {int} date starting point in years from todays date.
     * @param {bool} YMD year-month-date format
     */
    past: (years, start, YMD) => {
      if (!start) {
        start = 0;
      }
      const refDate = new Date();
      refDate.setFullYear(refDate.getFullYear() - start);

      const result = faker.date.past(years, refDate);
      if (YMD) {
        return sprintf("%04d-%02d-%02d",
          result.getFullYear(), result.getMonth() + 1, result.getDate());
      }
      return sprintf("%02d/%02d/%04d",
        result.getMonth() + 1, result.getDate(), result.getFullYear())
    },

    future: (years, start, YMD) => {
      if (!start) {
        start = 0;
      }
      const refDate = new Date();
      refDate.setFullYear(refDate.getFullYear() + start);

      const result = faker.date.future(years, refDate);
      if (YMD) {
        return sprintf("%04d-%02d-%02d",
          result.getFullYear(), result.getMonth() + 1, result.getDate());
      }
      return sprintf("%02d/%02d/%04d",
        result.getMonth() + 1, result.getDate(), result.getFullYear())
    }
  },

  finance: {
    /**
     * @param number {int} Num digits
     */
    account: (number) => {
      return faker.finance.account(number);
    },

    /**
     * Random account string
     * @param template {str} A string where each '#' is replaced with a digit
     */
    accountTemplate: template => {
      return faker.helpers.replaceSymbolWithNumber(template);
    },

    accountName: format => faker.finance.accountName(format),
  },

  random: {
    percent: () => {
      const value = faker.random.number({min: 0, max: 1, precision: 0.001 });
      return sprintf("%f%%", value * 100);
    },

    num: (min, max, precision = -1) => {
      if (precision === -1) {
        precision = 1;
      }
      return sprintf("%d", faker.random.number({min: min, max: max, precision }));
    },

    string: (chars, length) => {
      let result = "";
      for (let i=0; i < length; i++) {
        const num = faker.random.number({min: 0, max: chars.length - 1, precision: 1 });
        result += chars[num];
      }
      return result;
    },

    // Randomly shuffle two characters in a string, or two items in an array
    shuffle: (chars, num) => {
      const arr = chars.split("");
      for (let index=0; index < num; index++) {
        const i = faker.random.number({min: 0, max: chars.length - 1, precision: 1 });
        const j = faker.random.number({min: 0, max: chars.length - 1, precision: 1 });
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
      }
      return arr.join("");
    },

    // Creates a strong password using at least one lowercase, uppercase,
    // number and special character.
    password: (len) => {
      let password = "";
      password += ROOT.random.string(LOWERCASE, 1);
      password += ROOT.random.string(UPPERCASE, 1);
      password += ROOT.random.string(DIGITS, 1);
      password += ROOT.random.string(SPECIAL_CHARACTERS, 1);
      password += ROOT.random.string(ALL_CHARS, len - 4);
      password = ROOT.random.shuffle(password, len * 2);
      return password;
    }
  },

  // Just return a given value
  value: val => val,

  name: {
    name: () => {
      return faker.name.firstName() + " " + faker.name.lastName();
    },

    firstName: faker.name.firstName,

    lastName: faker.name.lastName,

    jobTitle: faker.name.jobTitle,
  },

  phone: {
    phoneNumber: faker.phone.phoneNumber,
  },

  address: {
    streetAddress: faker.address.streetAddress,
    city: faker.address.city,
    stateAbbr: faker.address.stateAbbr,
    zipCode: faker.address.zipCode,
    cityStateZip: () => {
      return faker.address.city() + ", " + faker.address.stateAbbr() + " " +
        faker.address.zipCode();
    },
    countryCode: faker.address.countryCode,
    country: faker.address.country,
  },

  company: {
    companyName: faker.company.companyName,
    bs: faker.company.bs,
  },

  internet: {
    email: faker.internet.email,
  },

  TDA: {
    industryOfOccupation: () => {
      const data = TDA_data.industry_of_occupation;
      const key = faker.random.arrayElement(Object.keys(data));
      return data[key];
    },

    occupation: () => {
      const data = TDA_data.occupation;
      const key = faker.random.arrayElement(Object.keys(data));
      return data[key];
    }
  },

  relationship: {
    relationship: () => {
      return faker.random.arrayElement(relationships);
    },
  },
};

const seed = num => {
  faker.seed(num);
};

// Seed using time to randomize
const seed_time = () => {
  faker.seed(Date.now() % 1000000);
}

/**
 * Gets a fake value
 * @param params {
 *   path: {str} A path to the fake function
 *   arg0: {?} First parameter if defined
 *   arg1: {?} Second parameter if defined
 * }
 * @return {{date: {past: (*|string)}}}
 */
const get_fake_value = (params) => {

  const segments = params.path.split(".");

  let current = ROOT;

  segments.forEach(segment => {
    current = current[segment];
  });

  return current(params.arg0, params.arg1, params.arg2);
};

export { ROOT, seed, get_fake_value, seed_time };

